import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './Icon';
import '../css/sort-menu.css';

const SortMenu = ({ 
  sort, 
  direction, 
  onSortChange, 
  onDirectionChange,
  sortOptions: customSortOptions,
  translationKey = 'orders'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Используем переданные опции или дефолтные для orders
  const sortOptions = customSortOptions || [
    { value: 'updatedAt', label: t(`${translationKey}.filters.sortByDate`) },
    { value: 'createdAt', label: t(`${translationKey}.filters.sortByCreatedDate`) },
    { value: 'userId', label: t(`${translationKey}.filters.userId`) }
  ];

  const directionOptions = [
    { value: 'DESC', label: t(`${translationKey}.filters.descending`), icon: '↓' },
    { value: 'ASC', label: t(`${translationKey}.filters.ascending`), icon: '↑' }
  ];

  const handleSortSelect = (value) => {
    onSortChange(value);
    setIsOpen(false);
  };

  const handleDirectionSelect = (value) => {
    onDirectionChange(value);
    setIsOpen(false);
  };

  return (
    <div className="sort-menu-wrapper" ref={menuRef}>
      <button
        type="button"
        className="sort-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t(`${translationKey}.filters.sortButton`)}
      >
        <span className="sort-menu-icon"><Icon name="sort-arrows" size={14} /></span>
        <span className="sort-menu-label">{t(`${translationKey}.filters.sortButton`)}</span>
        <span className="sort-menu-arrow"><Icon name="chevron-down" size={12} className={`expand-icon ${isOpen ? 'open' : ''}`} /></span>
      </button>

      <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="sort-menu-dropdown"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
        >
          <div className="sort-menu-section">
            <div className="sort-menu-section-title">{t(`${translationKey}.filters.sortBy`)}</div>
            <div className="sort-menu-options">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                  onClick={() => handleSortSelect(option.value)}
                >
                  <span className="sort-menu-option-label">{option.label}</span>
                  {sort === option.value && (
                    <span className="sort-menu-option-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="sort-menu-divider"></div>

          <div className="sort-menu-section">
            <div className="sort-menu-section-title">{t(`${translationKey}.filters.sortDirection`)}</div>
            <div className="sort-menu-options">
              {directionOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                  onClick={() => handleDirectionSelect(option.value)}
                >
                  <span className="sort-menu-option-icon">{option.icon}</span>
                  <span className="sort-menu-option-label">{option.label}</span>
                  {direction === option.value && (
                    <span className="sort-menu-option-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default SortMenu;
